import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { 
  Modal, 
  Input, 
  Button,
  Select,
  Table,
  Empty,
  DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import { UserOutlined, PhoneOutlined, MailOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { AudienceSetUpdate, SelectionCriteriaList, AudienceSetMembersList } from '../../api/Api';
import { Base64 } from 'js-base64';
import { useSelector } from 'react-redux';

import NotificationContext from '../../../../providers/NotificationProvider';
import { showSuccessToast, showErrorToast } from '../../../../components/commonComponents/gxAntdToast/toastUtils';
import { 
  FIELD_TYPES, 
  OPERATORS, 
  SPECIAL_FIELDS,
  FIELD_AS_CRITERIA,
  getFieldOptions,
  isSpecialCriteria,
  getOperators,
  getOperatorLabel,
  formatDisplayText,
} from '../../utils/fieldTypes';

const { TextArea } = Input;

const EditAudienceModal = ({ visible, onClose, onSuccess, audienceData }) => {
  const notification = useContext(NotificationContext);
  const { currentBrand } = useSelector((store) => store.app);

  // Form state
  const [name, setName] = useState(audienceData?.name || '');
  const [description, setDescription] = useState(audienceData?.description || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Criteria and fields
  const [criteriaData, setCriteriaData] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedCriteria, setSelectedCriteria] = useState('');
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedField, setSelectedField] = useState('');
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [selectedValue, setSelectedValue] = useState('');

  // Conditions state - Initialize with existing conditions if available
  const [conditions, setConditions] = useState(audienceData?.filters?.conditions || []);

  // Contacts table state
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalContacts, setTotalContacts] = useState(0);

  // Helper function to flatten nested conditions
  const flattenConditions = (conditions) => {
    if (!conditions) return [];

    // If it's an array, process each item
    if (Array.isArray(conditions)) {
      return conditions.flatMap(item => flattenConditions(item));
    }

    // If it's an object with nested conditions, process them
    if (conditions.conditions && Array.isArray(conditions.conditions)) {
      return flattenConditions(conditions.conditions);
    }

    // If it's a condition object with field, value, and operator, return it as a condition
    if (conditions.field && conditions.operator !== undefined && conditions.value !== undefined) {
      // Generate a unique ID if not present
      const condition = { ...conditions };
      if (!condition.id) {
        condition.id = Date.now() + Math.floor(Math.random() * 1000);
      }
      // Ensure we have a display_value
      if (!condition.display_value) {
        condition.display_value = condition.value;
      }
      // Set default logic to 'and' if not specified
      if (!condition.logic) {
        condition.logic = 'and';
      }
      return [condition];
    }

    // If it's a condition object with an id, return it
    if (conditions.id) {
      return [conditions];
    }
    return [];
  };

  // Initialize form with audience data when modal opens
  useEffect(() => {
    if (visible && audienceData) {
      setName(audienceData.name || '');
      setDescription(audienceData.description || '');

      // Process conditions from filters or selection_criteria
      let conditionsToSet = [];

      if (audienceData.filters) {
        // Check if filters is a direct condition object (has field, operator, value)
        if (audienceData.filters.field && audienceData.filters.operator !== undefined) {
          conditionsToSet = [{
            ...audienceData.filters,
            id: Date.now(),
            criteria: audienceData.filters.criteria,
            field: audienceData.filters.field,
            field_type: 'select', // Default field type
            logic: 'and', // Default logic
            display_value: audienceData.filters.display_value || audienceData.filters.value
          }];
        } 
        // Handle array of conditions
        else if (Array.isArray(audienceData.filters)) {
          conditionsToSet = flattenConditions(audienceData.filters);
        } 
        // Handle nested conditions object
        else if (audienceData.filters.conditions) {
          conditionsToSet = flattenConditions(audienceData.filters.conditions);
        }
      } else if (audienceData.selection_criteria?.conditions) {
        conditionsToSet = flattenConditions(audienceData.selection_criteria.conditions);
      }
      setConditions(conditionsToSet);
    } else if (visible) {
      setConditions([]);
    }
  }, [visible, audienceData]);

  // Modify handleSave to use update API
  const handleSave = async () => {
    if (!name.trim()) {
      showErrorToast(notification, 'Please enter a name for the audience');
      return;
    }

    if (conditions.length === 0) {
      showErrorToast(notification, 'Please add at least one condition');
      return;
    }

    setIsSubmitting(true);

    try {
      // Build the filter object for selection_criteria
      const selectionCriteria = buildFilterObject(conditions);
      // Build the audience payload
      const payload = {
        id: audienceData.id, // Include the audience ID for update
        name: name.trim(),
        description: description.trim(),
        selection_criteria: selectionCriteria,
        brand_id: currentBrand?.brand_id,
      };

      // Call the API to update the audience
      const response = await AudienceSetUpdate(currentBrand?.brand_id, payload);
      
      if (response.status === 200 || response.status === 201) {
        showSuccessToast(notification, response?.data?.message || 'Audience set updated successfully');
        
        // Close the modal
        onClose();
        // Call onSuccess callback to refresh audience list
        if (onSuccess) {
          await onSuccess();
        }
      } else {
        showErrorToast(notification, response?.response?.data?.error?.message || 'Failed to update audience');
      }
    } catch (error) {
      showErrorToast(notification, 'Failed to update audience');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the selected field's data
  const selectedFieldData = useMemo(() => {
    if (!selectedField || !availableFields.length) return null;
    // First try to match by id, then by value, then by name
    return availableFields.find(field => 
      field.id === selectedField || 
      field.value === selectedField ||
      field.name === selectedField
    );
  }, [selectedField, availableFields]);

  // Check if current criteria requires field selection
  const requiresFieldSelection = useMemo(() => {
    return selectedCriteria && !FIELD_AS_CRITERIA.includes(selectedCriteria);
  }, [selectedCriteria]);

  // Get the current field type
  const currentFieldType = useMemo(() => {
    if (selectedFieldData) {
      return selectedFieldData.type || 'text';
    }
    if (FIELD_AS_CRITERIA.includes(selectedCriteria)) {
      return 'select';
    }
    return 'text';
  }, [selectedFieldData, selectedCriteria]);

  // Fetch criteria data on component mount
  useEffect(() => {
    const fetchCriteria = async () => {
      try {
        const response = await SelectionCriteriaList(currentBrand?.brand_id);
        if (response?.data?.data) {
          setCriteriaData(response.data.data);
          
          // Transform the criteria data into the format needed for the dropdown
          const mainFields = Object.entries(response.data.data).map(([key, items]) => {
            return {
              label: key.split('_').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' '),
              value: key,
              type: 'criteria'
            };
          });
          
          setFields(mainFields);
        }
      } catch (error) {
        showErrorToast(notification, 'Failed to load selection criteria');
      }
    };

    if (currentBrand?.brand_id) {
      fetchCriteria();
    }
  }, [currentBrand?.brand_id]);

  // Handle criteria selection change
  const handleCriteriaChange = (value) => {
    setSelectedCriteria(value);
    setSelectedField('');
    setSelectedValue('');
    setSelectedOperator('');

    const criteriaItems = criteriaData?.[value] || [];    
    if (isSpecialCriteria(value)) {
      // For special criteria (tags, contact_origin, etc.)
      const formattedFields = criteriaItems.map((item, index) => ({
        id: item?.id || `${value}_${index}`,
        name: item?.name || formatLabel(value),
        value: item?.name,
        displayName: item?.name || formatLabel(value),
        type: item?.type || 'select',
        options: item?.options || [],
        ...item
      }));

      setAvailableFields(formattedFields);

      // Auto-select the first field if available
      if (formattedFields.length > 0) {
        const firstField = formattedFields[0];
        setSelectedField(firstField.value || firstField.id);
        
        // Set operators based on field type
        const fieldType = firstField.type || 'select';
        const fieldOperators = getOperators(fieldType);
        setOperators(fieldOperators);
        setSelectedOperator(fieldOperators[0]?.value || '');
      }
    } else {
      // For regular criteria with fields
      const formattedFields = criteriaItems.map((item, index) => ({
        id: item?.id,
        name: item?.name || formatLabel(item.field || item.id || `field_${index}`),
        value: item?.name || `${value}_${index}`,
        displayName: item?.name || formatLabel(item.field || item.id || `field_${index}`),
        type: item?.type || 'text',
        options: item?.options || [],
        scope: item?.scope,
        ...item
      }));

      setAvailableFields(formattedFields);

      // Auto-select the first field if available
      if (formattedFields.length > 0) {
        const firstField = formattedFields[0];
        setSelectedField(firstField.value || firstField.id);
        
        // Set operators based on field type
        const fieldType = firstField.type || 'text';
        const fieldOperators = getOperators(fieldType);
        setOperators(fieldOperators);
        setSelectedOperator(fieldOperators[0]?.value || '');
      }
    }
  };

  // Update operators when field is selected
  useEffect(() => {
    if (selectedField && availableFields.length > 0) {
      const field = availableFields.find(f => f.id === selectedField);
      if (field) {
        // Set appropriate operators based on field type
        let newOperators = [];
        switch(field.type) {
          case 'select':
          case 'tag':
            newOperators = [
              { label: 'is', value: 'is' },
              { label: 'is not', value: 'is_not' }
            ];
            break;
          case 'number_range':
            newOperators = [
              { label: 'is', value: 'is' },
              { label: 'is not', value: 'is_not' },
              { label: 'is greater than', value: 'gt' },
              { label: 'is less than', value: 'lt' },
            ];
            break;
          case 'date_range':
            newOperators = [
              { label: 'is', value: 'is' },
              { label: 'is before', value: 'before' },
              { label: 'is after', value: 'after' },
            ];
            break;
          case 'text':
          default:
            newOperators = [
              { label: 'is', value: 'is' },
              { label: 'is not', value: 'is_not' },
              { label: 'contains', value: 'contains' },
              { label: 'does not contain', value: 'not_contains' },
            ];
        }
        setOperators(newOperators);
        setSelectedOperator('');
        setSelectedValue('');
      }
    } else {
      // Reset to default operators when no field is selected
      setOperators([
        { label: 'is', value: 'is' },
        { label: 'is not', value: 'is_not' },
        { label: 'contains', value: 'contains' },
        { label: 'does not contain', value: 'not_contains' },
      ]);
      setSelectedOperator('');
      setSelectedValue('');
    }
  }, [selectedField, availableFields]);

  const handleAddCondition = () => {
    if (!selectedCriteria) {
      showErrorToast(notification, 'Please select a criteria');
      return;
    }

    const isSpecial = FIELD_AS_CRITERIA.includes(selectedCriteria);

    // Use the selectedFieldData for more reliable field lookup
    const fieldToUse = isSpecial ? selectedCriteria : selectedField;
    const fieldData = selectedFieldData || availableFields.find(f => 
      f.id === selectedField || f.value === selectedField || f.name === selectedField
    );

    if (!fieldToUse) {
      showErrorToast(notification, 'Please select a field');
      return;
    }

    if (!selectedOperator) {
      showErrorToast(notification, 'Please select an operator');
      return;
    }

    if (selectedValue === undefined || selectedValue === '' || (Array.isArray(selectedValue) && selectedValue.length === 0)) {
      showErrorToast(notification, 'Please select or enter a value');
      return;
    }

    // Use the fieldData we found earlier or try to find it again if not available
    const fieldDataToUse = fieldData || availableFields.find(f => 
      f.id === selectedField || f.value === selectedField || f.name === selectedField
    ) || {};

    // Get the field type
    const fieldType = isSpecial ? 'select' : fieldData.type || 'text';

    // Format the value based on the field type
    let formattedValue = selectedValue;
    let displayValue = selectedValue;

    if (fieldType === 'date_range' && Array.isArray(selectedValue)) {
      formattedValue = selectedValue.join(' to ');
      displayValue = formattedValue;
    } else if (fieldType === 'number' || fieldType === 'number_range') {
      formattedValue = parseFloat(selectedValue);
      displayValue = formattedValue;
    } else if (fieldType === 'boolean') {
      formattedValue = String(selectedValue).toLowerCase() === 'true';
      displayValue = formattedValue ? 'Yes' : 'No';
    } else if (fieldType === 'select' || fieldType === 'tag' || fieldType === 'multiselect') {
      // For select fields, try to find the display label
      const options = isSpecial 
        ? Array.isArray(criteriaData?.[selectedCriteria]) 
          ? criteriaData[selectedCriteria] 
          : []
        : Array.isArray(fieldData?.options) 
          ? fieldData.options 
          : [];

      if (Array.isArray(selectedValue)) {
        // Handle multiselect
        displayValue = selectedValue.map(val => {
          const option = options.find(opt => {
            // Handle both object with value property and direct value
            const optValue = opt?.value !== undefined ? opt.value : opt;
            return String(optValue) === String(val);
          });
          return option?.label || option?.name || option?.value || val;
        }).join(', ');
      } else {
        // Handle single select
        const option = options.find(opt => {
          // Handle both object with value property and direct value
          const optValue = opt?.value !== undefined ? opt.value : opt;
          return String(optValue) === String(selectedValue);
        });
        
        if (option) {
          displayValue = option.name || option.value || option;
        }
      }
    }

    // ---------------------------------
    const isSpecialCriteria = FIELD_AS_CRITERIA.includes(selectedCriteria);
    let conditionValue = formattedValue;

    // For fields with ID-based options (like tags, professions, etc.)
    if (selectedValue && fieldDataToUse) {
      // If it's a special criteria (like tags)
      if (isSpecialCriteria) {
        // For tags, use the selected tag's ID as the value
        if (selectedCriteria === 'tags') {
          // Find the selected tag from available fields
          const allTags = availableFields.filter(f => f.field === 'tags');
          const selectedTag = allTags.find(tag => tag.name === selectedValue);
          if (selectedTag?.id) {
            conditionValue = selectedTag.id;
            displayValue = selectedValue;
          }
        }
        else if (selectedCriteria === 'contact_category') {
          const allContactCategory = availableFields.filter(f => f.field === 'contact_category');
          const selectedContactCategory = allContactCategory.find(category => category.name === selectedValue);
          if (selectedContactCategory?.id) {
            conditionValue = selectedContactCategory.id;
            displayValue = selectedValue;
          }
        }
        else if (selectedCriteria === 'contact_source') {
          const allContactSource = availableFields.filter(f => f.field === 'contact_source');
          const selectedContactSource = allContactSource.find(source => source.name === selectedValue);
          if (selectedContactSource?.id) {
            conditionValue = selectedContactSource.id;
            displayValue = selectedValue;
          }
        }
        // For other special criteria with IDs, use the field's ID
        else if (fieldDataToUse?.id) {
          conditionValue = fieldDataToUse.id;
        }
      }
      // For regular fields with ID-based options
      else if (fieldDataToUse?.options?.length > 0 && 
              typeof fieldDataToUse.options[0] === 'object' && 
              'id' in fieldDataToUse.options[0]) {
        // Find the selected option by name (selectedValue)
        const selectedOption = fieldDataToUse.options.find(opt => 
          String(opt.name || opt.value || opt) === String(selectedValue)
        ) || {};
        
        // Use the ID as value if available, otherwise use the display value
        if (selectedOption?.id) {
          conditionValue = selectedOption.id;
          // displayValue = selectedOption.name || selectedOption.value || selectedOption;
        }
      }
    }

    // Special case: For contact_origin, always use the display value
    if (selectedCriteria === 'contact_origin') {
      conditionValue = selectedValue;
    }

    // For profile attributes, use attr_<id> as the field name for the backend
    const fieldName = isSpecial 
    ? selectedCriteria 
    : (fieldDataToUse?.field === 'profile_attributes' && fieldDataToUse?.id 
        ? `attr_${fieldDataToUse.id}`
        : fieldDataToUse?.field || fieldDataToUse?.name || fieldDataToUse?.value || '');

    // Create the new condition with the actual field name
    const newCondition = {
      id: Date.now(),
      criteria: selectedCriteria,
      field: fieldName,
      fieldId: fieldData.displayName || fieldData.name || fieldData.value || '', // This will be used for display in the UI,
      operator: selectedOperator,
      value: conditionValue,
      field_type: fieldType,
      display_value: displayValue,
      operatorLabel: getOperatorLabel(selectedOperator, fieldType),
      ...(fieldDataToUse?.scope && { scope: fieldDataToUse.scope })
    };

    setConditions([...conditions, newCondition]);
    setSelectedCriteria('');
    setSelectedField('');
    setSelectedOperator('');
    setSelectedValue('');
    setCurrentPage(1); // Reset to first page when adding a new condition
    setTotalContacts(0);
    return;
  }

  // Build filter object from conditions
  const buildFilterObject = (conditions) => {
    if (conditions.length === 0) return null;
    
    // If there's only one condition, return it directly
    if (conditions.length === 1) {
      const { criteria, field, operator, value, field_type, scope, display_value } = conditions[0];
      return {
        criteria,
        field: FIELD_AS_CRITERIA.includes(criteria) ? criteria.toLowerCase() : field?.toLowerCase(),
        operator,
        value,
        ...(display_value && { display_value }),
        ...(scope && { scope: typeof scope === 'string' ? scope.toLowerCase() : scope })
      };
    }

    // Create condition objects with their logic
    const conditionObjects = conditions.map((condition, index, array) => {
      const baseCondition = {
        criteria: condition.criteria,
        ...condition,
        field: FIELD_AS_CRITERIA.includes(condition.criteria) 
          ? condition.criteria.toLowerCase() 
          : condition.field?.toLowerCase()
      };

      // Include logic for all conditions except the last one
      if (index < array.length - 1) {
        return {
          ...baseCondition,
          logic: condition.logic || 'and'
        };
      }
      return baseCondition;
    });

    // Start with the first condition
    let result = conditionObjects[0];

    // Process each subsequent condition
    for (let i = 1; i < conditionObjects.length; i++) {
      const current = conditionObjects[i];
      const prevCondition = conditions[i - 1];
      const logic = prevCondition.logic || 'and';

      // Create a new group with the current logic
      result = {
        operator: logic,
        conditions: [result, current]
      };
    }
    return result;
  };

  // Fetch matching contacts based on conditions
  // Add a ref to track the last fetch time
  const lastFetchTimeRef = useRef(0);
  // Add a ref to track if we're currently fetching
  const isFetchingRef = useRef(false);

  const fetchMatchingContacts = async (page = 1, size = 10) => {
    // Don't allow multiple simultaneous requests
    if (isFetchingRef.current) return;

    // Prevent too frequent API calls (at least 500ms between calls)
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 500) return;
    lastFetchTimeRef.current = now;

    // Reset contacts if no conditions
    if (conditions.length === 0) {
      setContacts([]);
      setTotalContacts(0);
      return;
    }

    // Build the filter object
    const filterObject = buildFilterObject(conditions);

    // Don't call API if no valid filter object
    if (!filterObject || (filterObject.conditions && filterObject.conditions.length === 0)) {
      setContacts([]);
      setTotalContacts(0);
      return;
    }

    // Set loading state
    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      // Convert filter object to JSON and encode to base64
      const filtersJson = JSON.stringify(filterObject);
      const encodedFilters = Base64.encode(filtersJson);

      // Call the API with the encoded filters
      const response = await AudienceSetMembersList(
        currentBrand?.brand_id,
        `filters=${encodedFilters}`,
        audienceData?.added_contact_person_ids || [], // addedIds
        audienceData?.removed_contact_person_ids || [], // removedIds
        page,
        size
      );

      if (response.data?.data) {
        const { members, pagination } = response.data.data;
        setContacts(members || []);
        setTotalContacts(pagination?.total_count || 0);
        // Only update pagination if the data is different
        if (currentPage !== (pagination?.current_page || page) || 
            pageSize !== (pagination?.per_page || size)) {
          setCurrentPage(pagination?.current_page || page);
          setPageSize(pagination?.per_page || size);
        }
      } else {
        console.error('Error fetching contacts:', response);
        setContacts([]);
        setTotalContacts(0);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
      setTotalContacts(0);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  };

  // Handle logic operator change (AND/OR) between conditions
  const handleLogicChange = (id, logic) => {
    setConditions(prevConditions => {
      const newConditions = prevConditions.map(condition => 
        condition.id === id ? { ...condition, logic } : condition
      );
      return newConditions;
    });
  };
  
  // Remove a condition from the conditions list
  const handleRemoveCondition = (id) => {
    setConditions(prevConditions => prevConditions.filter(condition => condition.id !== id));
  };

  // Reset form when modal visibility changes
  useEffect(() => {
    if (visible && audienceData) {
      // Set initial conditions if they exist
      if (audienceData?.filtes?.conditions) {
        setConditions(audienceData?.filtes?.conditions);
      } else if (audienceData?.filtes) {
        // Handle case where conditions are at the root level
        setConditions(audienceData?.filtes);
      }
    }
  }, [visible, audienceData]);

  // Load contacts when conditions change
  useEffect(() => {
    if (conditions.length > 0) {
      // Always fetch the first page when conditions change
      fetchMatchingContacts(1, pageSize);
      // Update current page state to 1
      setCurrentPage(1);
    } else {
      setContacts([]);
      setTotalContacts(0);
    }
  }, [conditions, pageSize]); // Removed currentPage from dependencies to prevent infinite loop

  // Format label for display
  const formatLabel = (str) => {
    if (!str) return '';
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Render the appropriate input component based on field type
  const renderValueInput = () => {
    if (!selectedField || !selectedFieldData) {
      return <Input disabled placeholder="Select a field first" className="h-9" />;
    }

    const fieldType = currentFieldType;
    const isSpecial = FIELD_AS_CRITERIA.includes(selectedCriteria);

    // Get options for the field
    let options = [];

    // Check if this is a date-related field
    const isDateField = ['birth_date', 'date_of_birth'].includes(selectedField) || 
                       ['birth_date', 'date_of_birth'].includes(fieldType);

    if (isDateField) {
      // For date fields, we'll use the date picker, no options needed
      options = [];
    } else if (isSpecial) {
      // For special criteria, use the criteria items as options
      const criteriaItems = criteriaData?.[selectedCriteria] || [];
      options = getFieldOptions(selectedField, fieldType, criteriaItems);
    } else if (selectedFieldData.options) {
      // For regular fields with options
      options = getFieldOptions(selectedField, fieldType, selectedFieldData.options);
    } else if (selectedField === 'monthly_salary') {
      options = SPECIAL_FIELDS.monthly_salary;
    } else if (selectedField === 'annual_income') {
      options = SPECIAL_FIELDS.annual_income;
    }

    // Render the appropriate input based on field type
    switch (fieldType) {
      case 'multiselect':
      case 'tag':
      case 'select':
        return (
          <Select
            mode={fieldType === 'multiselect' ? 'multiple' : undefined}
            placeholder={`Select ${selectedFieldData.name || 'value'}`}
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option.children || '').toLowerCase().includes(input.toLowerCase())
            }
            value={selectedValue}
            onChange={setSelectedValue}
            className="w-full"
            allowClear
            showSearch
            dropdownStyle={{ minWidth: '200px' }}
            listHeight={400}
            menuItemSelectedIcon={null}
            getPopupContainer={trigger => trigger.parentNode}
          >
            {options?.map((option, index) => {
              const value = option.value || option;
              const label = option.label || option;
              return (
                <Select.Option 
                  key={`${value}-${index}`} 
                  value={value}
                >
                  {label}
                </Select.Option>
              );
            })}
          </Select>
        );
        
      case 'date':
        return (
          <DatePicker
            className="w-full"
            placeholder="Select date"
            value={selectedValue ? dayjs(selectedValue) : null}
            onChange={(date, dateString) => setSelectedValue(dateString)}
            format="YYYY-MM-DD"
          />
        );
        
      case 'date_range':
        return (
          <DatePicker.RangePicker
            className="w-full"
            value={selectedValue && selectedValue.length === 2 ? [
              dayjs(selectedValue[0]),
              dayjs(selectedValue[1])
            ] : [null, null]}
            onChange={(dates, dateStrings) => setSelectedValue(dateStrings)}
            format="YYYY-MM-DD"
          />
        );
        
      case 'number':
      case 'number_range':
        return (
          <Input
            type="number"
            placeholder={`Enter ${selectedFieldData.name || 'value'}`}
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            className="h-9"
          />
        );
        
      case 'birth_date':
      case 'date_of_birth':
      case 'birth_date': // Handle different field name variations
        return (
          <DatePicker
            className="w-full"
            placeholder={`Select ${selectedFieldData.name || 'birth date'}`}
            value={selectedValue ? dayjs(selectedValue) : null}
            onChange={(date, dateString) => setSelectedValue(dateString)}
            format="YYYY-MM-DD"
            disabledDate={(current) => {
              // Prevent selecting future dates for birth date
              return current && current > dayjs().endOf('day');
            }}
            allowClear
          />
        );
      case 'monthly_salary':
      case 'annual_income':
        return (
          <Select
            showSearch
            placeholder={`Select ${selectedFieldData.name || 'value'}`}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            value={selectedValue}
            onChange={setSelectedValue}
            className="w-full"
            options={options}
          />
        );
        
      case 'boolean':
        return (
          <Select
            value={selectedValue}
            onChange={setSelectedValue}
            className="w-full"
            options={[
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' }
            ]}
          />
        );
        
      default:
        return (
          <Input
            type="text"
            placeholder={`Enter ${selectedFieldData.name || 'value'}`}
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            className="h-9"
          />
        );
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    // Only update if the page or pageSize actually changed
    if (currentPage !== pagination.current || pageSize !== pagination.pageSize) {
      setCurrentPage(pagination.current);
      setPageSize(pagination.pageSize);
      // Use a small timeout to allow state to update before fetching
      setTimeout(() => {
        fetchMatchingContacts(pagination.current, pagination.pageSize);
      }, 0);
    }
  };

  const showTotal = (total, range) => {
    return `${range[0]}-${range[1]} of ${total} contacts`;
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <UserOutlined className="text-gray-400" />
          <span>
            {(() => {
              const formatName = (name) => name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
              const firstName = formatName(record.first_name || '');
              const lastName = record.last_name || '';
              const fullName = `${firstName} ${lastName}`.trim();
              return fullName || 'N/A';
            })()}
          </span>
        </div>
      ),
    },
    {
      title: 'Phone',
      key: 'phone',
      render: (_, record) => {
        const mobileContact = record.person_contacts?.find(
          contact => contact.contact_type === 'MOBILE_PHONE' && contact.priority === 1
        );
        const phoneNumber = mobileContact?.phone_number 
          ? `+${mobileContact.phone_country_code || '91'} ${mobileContact.phone_number}` 
          : 'N/A';
        return (
          <div className="flex items-center gap-2">
            <PhoneOutlined className="text-gray-400" />
            <span>{phoneNumber}</span>
          </div>
        );
      },
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => {
        const emailContact = record.person_contacts?.find(
          contact => contact.contact_type === 'EMAIL' && contact.priority === 1
        );
        const email = emailContact?.email || 'N/A';
        return (
          <div className="flex items-center gap-2">
            <MailOutlined className="text-gray-400" />
            <span className="truncate max-w-[200px] block">{email}</span>
          </div>
        );
      },
    },
    {
      title: 'Created At',
      key: 'created_at',
      render: (_, record) => dayjs(record.created_at).format('DD MMM YYYY, hh:mm A'),
    },
  ];

  return (
    <Modal
      title="Edit Audience Set"
      open={visible}
      onCancel={onClose}
      width="90%"
      style={{ top: 40, maxWidth: 1400 }}
      footer={[
        <Button 
          key="cancel" 
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>,
        <Button 
          key="update"
          type="primary" 
          onClick={handleSave}
          loading={isSubmitting}
          disabled={isSubmitting || !name || conditions.length === 0}
        >
          Update Audience
        </Button>
      ]}
      className="audience-modal"
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '0 24px'
        }
      }}
    >
      <div className="space-y-6 pb-6">
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <span className={`text-xs ${name.length > 100 ? 'text-red-500' : 'text-gray-500'}`}>
              {name.length}/100
            </span>
          </div>
          <Input 
            placeholder="Enter audience name" 
            value={name}
            onChange={(e) => {
              if (e.target.value.length <= 100) {
                setName(e.target.value);
              }
            }}
            status={name.length > 100 ? 'error' : ''}
            maxLength={100}
          />
          {name.length > 100 && (
            <p className="text-red-500 text-xs mt-1">Name must be 100 characters or less</p>
          )}
        </div>
        
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-gray-700">Description (optional)</label>
            <span className={`text-xs ${description.length > 1000 ? 'text-red-500' : 'text-gray-500'}`}>
              {description.length}/1000
            </span>
          </div>
          <TextArea 
            rows={3}
            placeholder="Enter a description for this audience"
            value={description}
            onChange={(e) => {
              if (e.target.value.length <= 1000) {
                setDescription(e.target.value);
              }
            }}
            maxLength={1000}
            className={description.length > 1000 ? 'border-red-500' : ''}
          />
          {description.length > 1000 && (
            <p className="text-red-500 text-xs mt-1">Description must be 1000 characters or less</p>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="space-y-3">
            <div className="space-y-3">
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <span className="font-medium">Conditions</span>
                {conditions.length > 0 && (
                  <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                    {conditions.length} {conditions.length === 1 ? 'condition' : 'conditions'}
                  </span>
                )}
              </div>

              {conditions.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-gray-400 text-sm">No conditions added yet</p>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  {conditions.map((condition, index) => (
                    <React.Fragment key={condition.id}>
                      <div className="relative group bg-white rounded border border-gray-200 p-2 pr-8">
                        <span className="text-sm text-gray-700">
                          {FIELD_AS_CRITERIA.includes(condition.criteria) ? (
                            <>
                              {formatDisplayText(condition.criteria)} {condition.operatorLabel || condition.operator} {condition.display_value}
                            </>
                          ) : (
                            <>{formatDisplayText(condition.criteria)}: {formatDisplayText(condition.fieldId)} {condition.operatorLabel || condition.operator} {condition.display_value}</>
                          )}
                        </span>
                        <Button
                          type="text"
                          icon={<CloseOutlined className="text-gray-400 hover:text-gray-600" />}
                          onClick={() => handleRemoveCondition(condition.id)}
                          size="small"
                          className="absolute right-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </div>
                      {index < conditions.length - 1 && (
                        <Select
                          value={condition.logic || 'and'}
                          onChange={(value) => handleLogicChange(condition.id, value)}
                          className="w-fit text-xs"
                          size="small"
                        >
                          <Select.Option value="and">AND</Select.Option>
                          <Select.Option value="or">OR</Select.Option>
                        </Select>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Add a condition</h4>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Criteria</label>
              <Select
                placeholder="Select criteria"
                className="w-full"
                value={selectedCriteria}
                onChange={handleCriteriaChange}
                options={fields}
                showSearch
                optionFilterProp="label"
                size="middle"
              />
            </div>

            {/* Field */}
            {requiresFieldSelection && (
              <div className="md:col-span-3">
                <label className="block text-xs text-gray-500 mb-1">Field</label>
                <Select
                  placeholder="Select field"
                  className="w-full"
                  value={selectedField}
                  onChange={(value) => {
                    const selectedFieldData = availableFields.find(f => f.id === value || f.value === value);
                    setSelectedField(value);
                    setSelectedValue('');
                    setSelectedOperator('');

                    // Update operators based on field type
                    if (selectedFieldData) {
                      const fieldType = selectedFieldData.type || 'text';
                      const fieldOperators = getOperators(fieldType);
                      setOperators(fieldOperators);
                      setSelectedOperator(fieldOperators[0]?.value || '');
                    }
                  }}
                  disabled={!selectedCriteria || availableFields.length === 0}
                  showSearch
                  optionFilterProp="label"
                  size="middle"
                >
                  {availableFields.map(field => (
                    <Select.Option 
                      key={field.id || field.value}
                      value={field.id || field.value}
                      fieldType={field.type}
                    >
                      {field.name || field.label || field.value}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {/* Operator */}
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Operator</label>
              <Select
                placeholder="Operator"
                className="w-full"
                value={selectedOperator}
                onChange={setSelectedOperator}
                disabled={!selectedField && !(selectedCriteria && FIELD_AS_CRITERIA.includes(selectedCriteria))}
                options={operators}
                size="middle"
              />
            </div>

            {/* Value */}
            <div className="md:col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Value</label>
              {renderValueInput()}
            </div>

            <div className="md:col-span-1 flex items-end">
              <Button 
                type="primary" 
                onClick={handleAddCondition}
                disabled={!selectedCriteria || !selectedOperator || !selectedValue || 
                  (requiresFieldSelection && !selectedField)}
                className="h-9 w-full flex items-center justify-center"
                icon={<PlusOutlined />}
              >
                Add
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-medium text-gray-700">Matching Contacts</h4>
            <span className="text-sm text-gray-500">{totalContacts} contacts match these criteria</span>
          </div>

          <div className="border rounded-md overflow-hidden">
            <Table
              columns={columns}
              dataSource={contacts}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalContacts,
                showSizeChanger: false,
                showTotal: showTotal,
                // pageSizeOptions: ['10', '20', '50', '100']
              }}
              loading={isLoading}
              onChange={handleTableChange}
              size="small"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      conditions.length === 0
                        ? 'Add conditions to see matching contacts'
                        : 'No contacts match the current conditions'
                    }
                  />
                )
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditAudienceModal;
